import React, { useContext, useEffect, useState } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import Footer from "../../components/Footer";
import Head from "../Head";
import { languageData } from "../../constants/languageData";
import { countries } from "../../constants/countries";
import JourneyConfig from "../../context/JourneyConfig";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import "./selectDocument.css";
import { useParams } from "react-router-dom";
import RenderSvg from "../../components/RenderSvg";
import { getDocumentFieldName } from "../../utils/documentFields";

function SelectCountry({ stepperFunc, steps, currentStep, currentDocumentStep = 0 }) {
    const { journeyId } = useParams();
    const { config, setConfig } = useContext(GlobalConfig);
    const { journey, setJourney, updateJourney, getJourney } =
        useContext(JourneyConfig);
    const { branding } = config ?? {};
    const bgColor =
    `color-mix(in srgb, ${branding?.accentColor} 5%, white 95%)`;
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const language = sessionStorage.getItem("language") ?? "en";
    // Which document this screen is asking about. Base owns the counter and advances it once a
    // document is analysed, so the same screen shown twice offers two different country lists
    // (IDX-677). A flow with one shared list only ever reaches this screen at document 0.
    const documentCapture = Array.isArray(config?.documentCapture)
        ? config?.documentCapture[currentDocumentStep]
        : config?.documentCapture;
    const { capturedData: journeyData } = journey ?? {};
    const [countryNames, setCountryNames] = useState(
        documentCapture?.countries
            ? documentCapture?.countries?.map((country) => country.country)
            : []
    );
    const [
        filteredCountriesFromOriginalFlow,
        setFilteredCountriesFromOriginalFlow,
    ] = useState(
        countryNames
            ? countries.filter((country) => {
                const labelMatch = countryNames.includes(country.label);
                const searchTermsMatch = country.searchTerms?.some(term =>
                    countryNames.includes(term)
                ) || false;
                return labelMatch || searchTermsMatch;
            })
            : []
    );
    const [filteredCountries, setFilteredCountries] = useState([
        ...filteredCountriesFromOriginalFlow,
    ]);
    const [selectedCountry, setSelectedCountry] = useState(
        // journeyData?.country
        //     ? countries.find((country) => country.value === journeyData.country)
        //     : null
        null
    );
    const [currentPageState, setCurrentPageState] = useState({
        // This document's own country, not the journey's first. The artwork beside the picker
        // switches once a country is chosen, so reading the unsuffixed field would show document 2
        // and 3 the "document chosen" illustration on a screen where nothing has been chosen yet.
        country: journey?.capturedData?.[getDocumentFieldName("country", currentDocumentStep)] ?? "",
        // idType: journey?.capturedData?.idType ?? "",
    });
    const [height, setHeight] = useState(window.innerHeight);
    const [width, setWidth] = useState(window.innerWidth);
    const isMobileView = height <= 800 && width<=720

    useEffect(() => {
        if (Object.keys(config).length) {
            const documentCapture = Array.isArray(config?.documentCapture)
                ? config?.documentCapture[currentDocumentStep]
                : config?.documentCapture;
            const cNames = documentCapture?.countries?.map(
                (country) => country.country
            ) ?? [];
            const filteredCountry = countries.filter((country) => {
                const labelMatch = cNames.includes(country.label);
                const searchTermsMatch = country.searchTerms?.some(term =>
                    cNames.includes(term)
                ) || false;
                return labelMatch || searchTermsMatch;
            });
            setCountryNames(cNames);
            setFilteredCountriesFromOriginalFlow(filteredCountry);
            setFilteredCountries([...filteredCountry]);
        }
    }, [config, currentDocumentStep]);

    useEffect(() => {
        if (journeyId && !journey) getJourney(journeyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journeyId]);

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase().trim();
        const filtered = filteredCountriesFromOriginalFlow.filter((country) => {
            const labelMatch = country.label.toLowerCase().includes(query);
            const searchTermsMatch = country.searchTerms?.some(term =>
                term.toLowerCase().includes(query)
            ) || false;
            return labelMatch || searchTermsMatch;
        });
        setFilteredCountries([...filtered]);
    };

    const handleBackButtonClick = () => {
        stepperFunc(steps[2]);
    };
    const changeHandler = (country) => {
        setSelectedCountry(country);
    };

    /**
     * A document type only means anything alongside the country it was offered for, so picking a
     * country drops the type choice of this document and of every document after it.
     *
     * Later documents are included because a flow sharing one country list across its documents
     * offers all of them the same types: changing the answer here changes what they may pick. A
     * flow asking per document has not reached those documents yet, so clearing them costs
     * nothing. Documents already captured come before this one and are left alone.
     */
    const clearedDocumentTypesFrom = (fromDocument) => {
        const totalDocuments = Array.isArray(config?.documentCapture)
            ? config.documentCapture.length
            : 1;
        const cleared = {};
        for (let index = fromDocument; index < totalDocuments; index++) {
            cleared[getDocumentFieldName("idType", index)] = "";
        }
        return cleared;
    };

    const updateJourneyAsync = async () => {
        setConfig({
            ...config,
            branding: { ...branding, countrySelected: selectedCountry },
        });
        sessionStorage.setItem('selectedCountry', selectedCountry?.value);
        // one-journey-backend counts documents from 1 and files the answer under country /
        // country2 / country3 accordingly; the screens count from 0. Sending the step keeps a
        // second document's country from overwriting the first one's (IDX-677).
        const response = await updateJourney("COUNTRY_SELECTION", {
            consent: true,
            country: selectedCountry?.value,
            currentDocumentStep: currentDocumentStep + 1,
        },currentStep);
        stepperFunc(steps?.[0] ?? currentStep+1);
        setJourney({
            ...journey,
            capturedData: {
                ...journeyData,
                // Mirrors what the backend just stored, so the document screen that renders next
                // reads this document's country rather than waiting for a refetch.
                [getDocumentFieldName("country", currentDocumentStep)]: selectedCountry?.value,
                ...clearedDocumentTypesFrom(currentDocumentStep),
            },
        });
    };

    useEffect(() => {
        if (selectedCountry) updateJourneyAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountry]);

    return (
        <>
            <Head
                // Back is offered only on the first document's country screen. Behind a later one
                // sits the previous document's analysis, which cannot be re-entered — the same
                // reason SelectDocument hides its own back icon after document one.
                showBackIcon={currentDocumentStep === 0}
                showCountry={false}
                handleBack={handleBackButtonClick}
                stepperFunc={stepperFunc}
                showLanguages={false}
                showMobileFlow={false}
                style={{ zIndex: "100" }}
            />
            <div
                className="parent-container"
                style={{
                    paddingTop: isMobileView  ? "70px" : "auto",
                }}
            >
                <div className="main">
                    <div
                        className="left-section"
                        style={{ display: isMobileView  ? "none" : "flex" }}
                    >
                        {!isMobileView && (
                            <div
                                style={{
                                    display: currentPageState?.country ? "block" : "none",
                                }}
                            >
                                <RenderSvg
                                    config={config}
                                    svgType={"selectDocument"}
                                    id="selectDocument"
                                />
                            </div>
                        )}
                        {!isMobileView && (
                            <div
                                style={{
                                    display: !currentPageState?.country ? "block" : "none",
                                }}
                            >
                                <RenderSvg
                                    config={config}
                                    id={"selectCountry"}
                                    svgType={"selectCountry"}
                                    className="md:w-[200px] md:h-[200px] w-[180px] h-[180px] md:relative fixed"
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className="right-section"
                        style={{
                            height: isMobileView  ? "100vh" : "auto",
                            borderRadius: isMobileView  ? "0" : "auto",
                            boxShadow: isMobileView  ? "none" : "auto",
                            justifyContent: isMobileView  ? "space-between" : "flex-end",
                        }}
                    >
                        <div className="flex flex-col">
                            <div className="text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start md:mb-[32px] mb-[20px]">
                                {
                                    languageData[language]?.documentationList
                                        ?.countryHeading
                                }
                            </div>
                            <div className="search-container flex h-[54px] items-center self-stretch">
                                <MagnifyingGlassIcon className="search-icon w-[24px] h-[24px]" />
                                <input
                                    type="text"
                                    id="floating_outlined"
                                    // disabled={journey?.data?.country}
                                    onChange={handleSearch}
                                    className="relative peer appearance-none block w-full text-gray-700 border border-gray-200 rounded pl-[36px] pr-[14px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500 inputFields"
                                    placeholder={languageData[language]?.documentationList?.searchCountry ?? "Search Country"}
                                    defaultValue={
                                        selectedCountry
                                            ? `${selectedCountry.logo} ${selectedCountry.label}`
                                            : ""
                                    }
                                    style={{ borderRadius: "10px" }}
                                    key={selectedCountry}
                                />
                            </div>
                            {!selectedCountry && (
                                <div
                                    className="mt-[18px] scrollbar md:h-[15rem] mb-[25px]"
                                    style={{ overflowY: "auto", maxHeight: "48vh" }}
                                >
                                    {filteredCountries.map((country, index) => (
                                        <div key={index}>
                                        <div
                                            onClick={() => changeHandler(country)}
                                            className="country-item"
                                            onMouseEnter={() => setHoveredIndex(index)} // Set the hovered index on mouse enter
                                            onMouseLeave={() => setHoveredIndex(null)}  // Reset the hovered index on mouse leave
                                            style={{
                                            backgroundColor: hoveredIndex === index ? bgColor : 'transparent', // Apply red if hovered
                            
                                            }}
                                        >
                                            <span className="country-logo">{country.logo}</span>
                                            <span className="country-label">{country.label}</span>
                                        </div>
                                        <hr />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Footer style={{ marginTop: "12px" }} />
                    </div>
                </div>
            </div>
        </>
    );
}

export default SelectCountry;
